import { Contact } from '@/api';
import {
    Dispatch,
    ReactElement,
    SetStateAction,
    useCallback,
    useEffect,
    useMemo,
    useState
} from 'react';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { UIFormInput, UIIcon, UIPhoneInput, UIText, UITextButton } from '@/ui';
import { moonlight } from '@/ui/styles';
import { uuidv4 } from '../util';
import { useRegion } from '../providers/region-provider';
import styles from './contacts-list.module.scss';
import { useCommonTranslation } from '../hooks';

type RoleOrRelationship = 'role' | 'relationship';

type ContactsListProps = {
    contacts: Contact[];
    roleOrRelationship?: RoleOrRelationship;
};
export function ContactsList(props: ContactsListProps): ReactElement {
    const { t } = useCommonTranslation();
    const { contacts, roleOrRelationship = 'role' } = props;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
            {contacts.map((contact, index) => (
                <ContactItem
                    key={index}
                    contact={contact}
                    roleOrRelationship={roleOrRelationship}
                    index={index}
                />
            ))}
            {contacts.length === 0 && (
                <UIText type="default">
                    {t('components.contacts_list.empty')}
                </UIText>
            )}
        </div>
    );
}

function ContactItem(props: {
    contact: Contact;
    index: number;
    roleOrRelationship: RoleOrRelationship;
}): ReactElement {
    const { contact, index, roleOrRelationship } = props;
    const { t } = useCommonTranslation();

    const roleOrRelationShipLabel =
        roleOrRelationship === 'role'
            ? t('components.contacts_list.role_label')
            : t('components.contacts_list.relationship_label');

    return (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <UIText>{index + 1}.</UIText>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <UIText medium>
                    {t('components.contacts_list.name_label')}{' '}
                    <UIText type="default">{contact.name}</UIText>
                </UIText>
                <UIText medium>
                    {roleOrRelationShipLabel}{' '}
                    <UIText type="default">{contact.role}</UIText>
                </UIText>
                <UIText medium>
                    {t('components.contacts_list.phone_label')}{' '}
                    <a
                        style={{ textDecoration: 'none', color: 'inherit' }}
                        href={`tel:${contact.phone}`}
                    >
                        <UIText type="link">{contact.phone}</UIText>
                    </a>
                </UIText>
            </div>
        </div>
    );
}

type EditableContactsListProps = {
    contacts: Array<ContactData>;
    setContacts: Dispatch<SetStateAction<ContactData[]>>;
    showRealTimeErrors: boolean;
    canEdit: boolean;
    roleOrRelationship?: RoleOrRelationship;
};
export function EditableContactsList(
    props: EditableContactsListProps
): ReactElement {
    const { t } = useCommonTranslation();
    const {
        contacts,
        showRealTimeErrors,
        canEdit,
        setContacts,
        roleOrRelationship
    } = props;

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates
        })
    );

    const onContactChange = useCallback(
        (index: number, data: ContactData) => {
            setContacts((prev) => {
                const newContacts = [...prev];
                newContacts[index] = data;
                return newContacts;
            });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const shouldRenderAddNewContactButton = useMemo(() => {
        if (!canEdit) {
            return false;
        }
        return contacts.length < 20;
    }, [canEdit, contacts]);

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 32
            }}
        >
            {contacts.length > 1 && canEdit ? (
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                >
                    <SortableContext
                        items={contacts}
                        strategy={verticalListSortingStrategy}
                    >
                        {contacts.map((contact, index) => (
                            <SortableContactInput
                                key={contact.id}
                                id={contact.id}
                                initialName={contact.name}
                                initialRole={contact.role}
                                initialPhone={contact.phone}
                                index={index}
                                canEdit={canEdit}
                                showRealTimeErrors={showRealTimeErrors}
                                roleOrRelationship={roleOrRelationship}
                                onChange={onContactChange}
                                onRemove={() => {
                                    setContacts((prev) =>
                                        prev.filter((_, i) => i !== index)
                                    );
                                }}
                            />
                        ))}
                    </SortableContext>
                </DndContext>
            ) : (
                contacts.map((contact, index) => (
                    <ContactInput
                        key={contact.id}
                        id={contact.id}
                        initialName={contact.name}
                        initialRole={contact.role}
                        initialPhone={contact.phone}
                        index={index}
                        canEdit={canEdit}
                        showRealTimeErrors={showRealTimeErrors}
                        roleOrRelationship={roleOrRelationship}
                        onChange={onContactChange}
                        onRemove={() => {
                            setContacts((prev) =>
                                prev.filter((_, i) => i !== index)
                            );
                        }}
                    />
                ))
            )}

            {shouldRenderAddNewContactButton && (
                <div>
                    <UITextButton
                        type="button"
                        variant="primary"
                        onClick={() => {
                            setContacts((prev) => [
                                ...prev,
                                {
                                    id: uuidv4(),
                                    name: '',
                                    role: '',
                                    phone: '',
                                    isValid: false
                                }
                            ]);
                        }}
                    >
                        {contacts.length === 0
                            ? t('components.contacts_list.add_contact')
                            : t('components.contacts_list.add_another')}
                    </UITextButton>
                </div>
            )}
            {!canEdit && contacts.length === 0 && (
                <div>
                    <UIText type="default2">
                        {t('components.contacts_list.empty')}
                    </UIText>
                </div>
            )}
        </div>
    );

    function handleDragEnd(event: DragEndEvent) {
        const { active, over } = event;

        if (active.id !== over?.id) {
            setContacts((items) => {
                const oldIndex = items.findIndex(
                    (item) => item.id === active.id
                );
                const newIndex = items.findIndex(
                    (item) => item.id === over?.id
                );

                return arrayMove(items, oldIndex, newIndex);
            });
        }
    }
}

export type ContactData = Contact & {
    id: string;
    isValid: boolean;
};

type ContactInputProps = {
    id: string;
    initialName: string;
    initialRole: string;
    initialPhone: string;
    isDragging?: boolean;
    index: number;
    canEdit: boolean;
    showRealTimeErrors: boolean;
    roleOrRelationship?: RoleOrRelationship;
    onChange: (index: number, data: ContactData) => void;
    onRemove: () => void;
};
function ContactInput(props: ContactInputProps): ReactElement {
    const { t } = useCommonTranslation();
    const {
        id,
        initialName,
        initialRole,
        initialPhone,
        isDragging = false,
        index,
        canEdit,
        showRealTimeErrors,
        roleOrRelationship = 'role',
        onChange,
        onRemove
    } = props;

    const { resolvedRegion } = useRegion();
    const defaultDialCode = resolvedRegion === 'us' ? '+1' : '+44';
    const lockDialCode = resolvedRegion === 'us' ? '+1' : undefined;

    /**
     * Name
     */
    const [name, setName] = useState(initialName);
    const checkName = useCallback(
        (name: string): string => {
            const trimmed = name.trim();
            if (trimmed.length === 0) {
                return t('components.contacts_list.error_name_required');
            } else if (trimmed.length > 100) {
                return t('components.contacts_list.error_name_too_long');
            }
            return '';
        },
        [t]
    );
    const nameError = useMemo(() => {
        if (!showRealTimeErrors) {
            return '';
        }
        return checkName(name);
    }, [checkName, name, showRealTimeErrors]);

    const roleOrRelationshipText = useMemo(() => {
        return roleOrRelationship === 'role'
            ? t('components.contacts_list.role_field_label')
            : t('components.contacts_list.relationship_field_label');
    }, [roleOrRelationship, t]);
    const roleOrRelationshipPlaceholder = useMemo(() => {
        return roleOrRelationship === 'role'
            ? t('components.contacts_list.role_placeholder')
            : t('components.contacts_list.relationship_placeholder');
    }, [roleOrRelationship, t]);

    /**
     * Role/Relationship
     */
    const [role, setRole] = useState(initialRole);
    const checkRole = useCallback(
        (role: string): string => {
            const trimmed = role.trim();
            if (trimmed.length === 0) {
                return roleOrRelationship === 'role'
                    ? t('components.contacts_list.error_role_required')
                    : t('components.contacts_list.error_relationship_required');
            } else if (trimmed.length > 100) {
                return roleOrRelationship === 'role'
                    ? t('components.contacts_list.error_role_too_long')
                    : t('components.contacts_list.error_relationship_too_long');
            }
            return '';
        },
        [roleOrRelationship, t]
    );
    const roleError = useMemo(() => {
        if (!showRealTimeErrors) {
            return '';
        }
        return checkRole(role);
    }, [checkRole, role, showRealTimeErrors]);

    /**
     * Phone
     */
    const [initialContactDialCode, initialContactPhone] = useMemo(() => {
        const phone = parsePhoneNumberFromString(initialPhone);

        if (!phone || !phone.isValid()) {
            return [lockDialCode ?? defaultDialCode, ''];
        }

        const parsedDialCode = `+${phone.countryCallingCode}`;
        return [lockDialCode ?? parsedDialCode, phone.nationalNumber];
    }, [initialPhone, defaultDialCode, lockDialCode]);
    const [dialCode, setDialCode] = useState(initialContactDialCode);
    const [phone, setPhone] = useState(initialContactPhone);
    const [isPhoneValid, setIsPhoneValid] = useState(false);

    useEffect(() => {
        const contact: ContactData = {
            id,
            name,
            role,
            phone: `${dialCode}${phone}`,
            isValid: !checkName(name) && !checkRole(role) && isPhoneValid
        };

        onChange(index, contact);
    }, [
        id,
        index,
        dialCode,
        isPhoneValid,
        name,
        nameError,
        onChange,
        checkName,
        checkRole,
        phone,
        role,
        roleError
    ]);

    return (
        <div className={styles.container}>
            <div className={styles.headerContainer}>
                <UIText medium>Contact {isDragging ? '#' : index + 1}.</UIText>
                {canEdit && (
                    <UITextButton
                        variant="danger"
                        type="button"
                        onClick={onRemove}
                    >
                        {t('shared.labels.remove')}
                    </UITextButton>
                )}
            </div>

            <div className={styles.nameAndRoleAndPhoneContainer}>
                <div className={styles.nameAndRoleContainer}>
                    <UIFormInput
                        name="name"
                        value={name}
                        placeholder={t(
                            'components.contacts_list.name_placeholder'
                        )}
                        label={t('components.contacts_list.name_field_label')}
                        type="text"
                        autoComplete="on"
                        errorText={nameError}
                        required={canEdit}
                        maxLength={100}
                        onChange={setName}
                        disabled={!canEdit}
                    />

                    <UIFormInput
                        name={roleOrRelationshipText}
                        value={role}
                        placeholder={roleOrRelationshipPlaceholder}
                        label={roleOrRelationshipText}
                        type="text"
                        autoComplete="on"
                        errorText={roleError}
                        required={canEdit}
                        maxLength={100}
                        onChange={setRole}
                        disabled={!canEdit}
                    />
                </div>

                <UIPhoneInput
                    name="phone"
                    label={t('components.contacts_list.phone_field_label')}
                    initialDialCode={initialContactDialCode}
                    initialNumber={initialContactPhone}
                    lockDialCode={lockDialCode}
                    required={canEdit}
                    showRealTimeErrors={showRealTimeErrors}
                    disabled={!canEdit}
                    onChange={(newDialCode, newNumber, isValid) => {
                        setDialCode(newDialCode);
                        setPhone(newNumber);
                        setIsPhoneValid(isValid);
                    }}
                />
            </div>
        </div>
    );
}

function SortableContactInput(
    props: ContactInputProps & { id: string }
): ReactElement {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging
    } = useSortable({ id: props.id });

    const style = {
        transform: CSS.Translate.toString(transform),
        transition,
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        zIndex: isDragging ? 1000 : 'auto',
        padding: isDragging ? 8 : 0,
        boxShadow: isDragging ? '0 0 10px rgba(0, 0, 0, 0.2)' : 'none',
        borderRadius: 8,
        backgroundColor: 'white'
    };

    return (
        <div ref={setNodeRef} style={style} {...attributes}>
            <ContactInput {...props} isDragging={isDragging} />
            <div
                {...listeners}
                style={{
                    paddingLeft: 8,
                    paddingRight: 8,
                    paddingTop: 32,
                    paddingBottom: 32,
                    height: '100%',
                    backgroundColor: moonlight,
                    borderRadius: 8,
                    cursor: 'grab'
                }}
            >
                <UIIcon name="align-justify" />
            </div>
        </div>
    );
}
